import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { StripeCheckout } from '../components/StripeCheckout';
import { PLAN_TYPES, BILLING_PERIODS, type PlanType, type BillingPeriod } from '../services/stripeService';
import { CURRENT_APP_PRICING } from '../services/stripeService';
import Layout from '../components/Layout';

const StripePaywallPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, initializing, isAuthenticated } = useAuth();
  const { userPlan, isExpired } = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(PLAN_TYPES.STANDARD);
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>(BILLING_PERIODS.MONTHLY);
  const [showCheckout, setShowCheckout] = useState(false);

  // 사용자 인증 확인 및 리다이렉트
  useEffect(() => {
    console.log('🔍 [StripePaywallPage] Auth state check:', {
      authLoading,
      initializing,
      user: user ? `User ${user.uid}` : 'No user',
      isAuthenticated,
      userExists: !!user
    });

    // 초기화 중이거나 인증 로딩 중이면 대기
    if (initializing || authLoading) {
      console.log('⏳ [StripePaywallPage] Auth initializing/loading, waiting...');
      return;
    }

    // 초기화와 로딩이 완료되었지만 사용자가 없는 경우에만 리다이렉트
    if (!initializing && !authLoading && !user) {
      console.log('❌ [StripePaywallPage] Auth completed but no user, redirecting to login');
      // 현재 URL을 state로 전달하여 로그인 후 돌아올 수 있도록 함
      const currentPath = location.pathname + location.search;
      navigate('/login', { 
        state: { 
          from: currentPath,
          message: t('paywall.authRequired')
        } 
      });
      return;
    }

    if (user) {
      console.log('✅ [StripePaywallPage] User authenticated, staying on paywall');
    }

    // 이미 활성 구독이 있는 사용자도 paywall에 머물 수 있도록 허용
    // (플랜 변경, 구독 갱신 등을 위해)
    // 단, expired 상태는 제외하지 않음 (구독 갱신을 위해 paywall에 머물 수 있음)
    // if (userPlan && userPlan !== 'trial' && userPlan !== 'expired') {
    //   navigate('/dashboard', { 
    //     replace: true,
    //     state: { 
    //       message: t('paywall.alreadySubscribed', '이미 구독 중입니다.')
    //     }
    //   });
    // }
  }, [user, userPlan, navigate, location, t, authLoading, initializing]);

  // 플랜 선택 처리
  const handlePlanSelect = (plan: PlanType) => {
    setSelectedPlan(plan);
  };

  // 결제 기간 선택 처리
  const handlePeriodSelect = (period: BillingPeriod) => {
    setSelectedPeriod(period);
  };

  // 구독 시작
  const handleStartSubscription = () => {
    if (!user) {
      // 로그인되지 않은 경우 로그인 페이지로 리다이렉트
      const currentPath = location.pathname + location.search;
      navigate('/login', { 
        state: { 
          from: currentPath,
          message: t('paywall.authRequired')
        } 
      });
      return;
    }
    setShowCheckout(true);
  };

  // 체크아웃 성공 처리
  const handleCheckoutSuccess = () => {
    setShowCheckout(false);
    alert(
      t('paywall.purchaseSuccess')
    );
  };

  // 체크아웃 취소 처리
  const handleCheckoutCancel = () => {
    setShowCheckout(false);
  };

  // 가격 표시 (USD)
  const getPriceDisplay = (plan: PlanType, period: BillingPeriod) => {
    const amount = CURRENT_APP_PRICING[plan.toUpperCase() as keyof typeof CURRENT_APP_PRICING][period];
    const dollars = amount / 100;
    return `US$${dollars.toFixed(2)}`;
  };

  // 플랜별 기능 표시
  const getPlanFeatures = (plan: PlanType) => {
    switch (plan) {
      case PLAN_TYPES.CARD_ONLY:
        return [
          { feature: t('paywall.features.cards2'), included: true },
          { feature: t('paywall.features.unlimitedQR'), included: true },
          { feature: t('paywall.features.qrLogo'), included: true },
          { feature: t('paywall.features.noInvoice'), included: false },
          { feature: t('paywall.features.noStatistics'), included: false },
        ];
      case PLAN_TYPES.STANDARD:
        return [
          { feature: t('paywall.features.cards5'), included: true },
          { feature: t('paywall.features.invoicesMonth8'), included: true },
          { feature: t('paywall.features.unlimitedQR'), included: true },
          { feature: t('paywall.features.qrLogo'), included: true },
          { feature: t('paywall.features.cardSpecialSettings'), included: true },
          { feature: t('paywall.features.basicStatistics'), included: true },
        ];
      case PLAN_TYPES.PRO:
        return [
          { feature: t('paywall.features.unlimitedCards'), included: true },
          { feature: t('paywall.features.unlimitedInvoices'), included: true },
          { feature: t('paywall.features.customDomain'), included: true },
          { feature: t('paywall.features.unlimitedQR'), included: true },
          { feature: t('paywall.features.advancedStatistics'), included: true },
          { feature: t('paywall.features.prioritySupport'), included: true },
        ];
      default:
        return [];
    }
  };

  // 플랜 이름 표시
  const getPlanName = (plan: PlanType) => {
    switch (plan) {
      case PLAN_TYPES.CARD_ONLY:
        return t('plan.card_only');
      case PLAN_TYPES.STANDARD:
        return t('subscription.plans.standard');
      case PLAN_TYPES.PRO:
        return t('subscription.plans.pro');
      default:
        return plan;
    }
  };

  // 초기화 중이거나 인증 로딩 중일 때 로딩 표시
  if (initializing || authLoading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  // 초기화와 로딩이 완료되었지만 사용자가 없는 경우
  if (!initializing && !authLoading && !user) {
    return (
      <Layout>
        <div className="auth-required">
          <h2>{t('paywall.authRequired')}</h2>
          <p>{t('paywall.loginRequired')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="stripe-paywall-page">
        <div className="header">
          <h1>{t('landing.pricing.title')}</h1>
          {isExpired ? (
            <div className="expired-notice">
              <p className="expired-text">{t('subscription.expired.message')}</p>
            </div>
          ) : (
            <p>{t('pricing.subtitle')}</p>
          )}
        </div>

        {/* 결제 기간 선택 */}
        <div className="billing-period-selector">
         
          <div className="period-options">
            <button
              className={`period-btn ${selectedPeriod === BILLING_PERIODS.MONTHLY ? 'active' : ''}`}
              onClick={() => handlePeriodSelect(BILLING_PERIODS.MONTHLY)}
            >
              {t('paywall.period.monthly')}
            </button>
            <button
              className={`period-btn ${selectedPeriod === BILLING_PERIODS.YEARLY ? 'active' : ''}`}
              onClick={() => handlePeriodSelect(BILLING_PERIODS.YEARLY)}
            >
              {t('paywall.period.yearly')}
            </button>
          </div>
        </div>

        {/* 플랜 선택 */}
        <div className="plans-container">
          <div className="plan-grid">
            {/* Card Only Plan */}
            <div className={`plan-card ${selectedPlan === PLAN_TYPES.CARD_ONLY ? 'selected' : ''}`}>
              <div className="plan-header">
                <h3>{getPlanName(PLAN_TYPES.CARD_ONLY)}</h3>
                <div className="plan-price">
                  {getPriceDisplay(PLAN_TYPES.CARD_ONLY, selectedPeriod)}
                  <span className="period-text">
                    {selectedPeriod === BILLING_PERIODS.MONTHLY ? t('paywall.perMonth') : t('paywall.perYear')}
                  </span>
                </div>
              </div>
              <div className="plan-features">
                {getPlanFeatures(PLAN_TYPES.CARD_ONLY).map((feature, index) => (
                  <div key={index} className={`feature ${feature.included ? 'included' : 'excluded'}`}>
                    {feature.included ? '✓' : '✗'} {feature.feature}
                  </div>
                ))}
              </div>
              <button
                className="select-plan-btn"
                onClick={() => handlePlanSelect(PLAN_TYPES.CARD_ONLY)}
              >
                {selectedPlan === PLAN_TYPES.CARD_ONLY ? t('invoice.creation.optional') : t('paywall.subscribe')}
              </button>
            </div>

            {/* Standard Plan */}
            <div className={`plan-card ${selectedPlan === PLAN_TYPES.STANDARD ? 'selected' : ''}`}>
              <div className="plan-header">
                <h3>{getPlanName(PLAN_TYPES.STANDARD)}</h3>
                <div className="plan-price">
                  {getPriceDisplay(PLAN_TYPES.STANDARD, selectedPeriod)}
                  <span className="period-text">
                    {selectedPeriod === BILLING_PERIODS.MONTHLY ? t('paywall.perMonth') : t('paywall.perYear')}
                  </span>
                </div>
              </div>
              <div className="plan-features">
                {getPlanFeatures(PLAN_TYPES.STANDARD).map((feature, index) => (
                  <div key={index} className={`feature ${feature.included ? 'included' : 'excluded'}`}>
                    {feature.included ? '✓' : '✗'} {feature.feature}
                  </div>
                ))}
              </div>
              <button
                className="select-plan-btn"
                onClick={() => handlePlanSelect(PLAN_TYPES.STANDARD)}
              >
                {selectedPlan === PLAN_TYPES.STANDARD ? t('invoice.creation.optional') : t('paywall.subscribe')}
              </button>
            </div>

            {/* Pro Plan */}
            <div className={`plan-card ${selectedPlan === PLAN_TYPES.PRO ? 'selected' : ''}`}>
              <div className="plan-header">
                <h3>{getPlanName(PLAN_TYPES.PRO)}</h3>
                <div className="plan-price">
                  {getPriceDisplay(PLAN_TYPES.PRO, selectedPeriod)}
                  <span className="period-text">
                    {selectedPeriod === BILLING_PERIODS.MONTHLY ? t('paywall.perMonth') : t('paywall.perYear')}
                  </span>
                </div>
              </div>
              <div className="plan-features">
                {getPlanFeatures(PLAN_TYPES.PRO).map((feature, index) => (
                  <div key={index} className={`feature ${feature.included ? 'included' : 'excluded'}`}>
                    {feature.included ? '✓' : '✗'} {feature.feature}
                  </div>
                ))}
              </div>
              <button
                className="select-plan-btn"
                onClick={() => handlePlanSelect(PLAN_TYPES.PRO)}
              >
                {selectedPlan === PLAN_TYPES.PRO ? t('invoice.creation.optional') : t('paywall.subscribe')}
              </button>
            </div>
          </div>
        </div>

        {/* 구독 시작 버튼 */}
        <div className="subscription-actions">
          <button
            className="start-subscription-btn"
            onClick={handleStartSubscription}
            disabled={!selectedPlan || !selectedPeriod}
          >
            {t('paywall.subscribe')}
          </button>
        </div>

        {/* 체크아웃 컴포넌트 */}
        {showCheckout && (
          <div className="checkout-modal">
            <div className="checkout-content">
              <h3>{t('payment.processing')}</h3>
              <StripeCheckout
                planType={selectedPlan}
                billingPeriod={selectedPeriod}
                userId={user?.uid || ''}
                onSuccess={handleCheckoutSuccess}
                onCancel={handleCheckoutCancel}
              />
            </div>
          </div>
        )}
      </div>


      <style>{`
        /* 헤더 스타일 */
        .paywall-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 40px 20px;
          text-align: center;
        }

        .header-content h1 {
          font-size: 2.5rem;
          margin-bottom: 10px;
          font-weight: 700;
        }

        .header-content p {
          font-size: 1.2rem;
          opacity: 0.9;
        }

        /* 메인 콘텐츠 */
        .stripe-paywall-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .header {
          text-align: center;
          margin-bottom: 40px;
          padding: 40px 0;
        }

        .header h1 {
          font-size: 2.5rem;
          color: #2c3e50;
          margin-bottom: 16px;
        }

        .header p {
          font-size: 1.2rem;
          color: #7f8c8d;
        }

        .expired-notice {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 16px;
          margin: 16px 0;
        }

        .expired-text {
          color: #dc2626;
          font-size: 1.1rem;
          font-weight: 500;
          margin: 0;
        }

        .billing-period-selector {
          text-align: center;
          margin-bottom: 40px;
        }

        .billing-period-selector h3 {
          margin-bottom: 20px;
          color: #2c3e50;
        }

        .period-options {
          display: flex;
          gap: 16px;
          justify-content: center;
        }

        .period-btn {
          padding: 12px 24px;
          border: 2px solid #e5e7eb;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
          transition: all 0.2s;
          position: relative;
        }

        .period-btn.active {
          border-color: #059669;
          background: #059669;
          color: white;
        }

        .discount-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background: #ef4444;
          color: white;
          font-size: 12px;
          padding: 4px 8px;
          border-radius: 12px;
          font-weight: 600;
        }

        .plans-container {
          margin-bottom: 40px;
        }

        .plan-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .plan-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          border: 2px solid #e5e7eb;
          transition: all 0.2s;
          position: relative;
        }

        .plan-card.selected {
          border-color: #059669;
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.2);
        }

        .plan-header {
          text-align: center;
          margin-bottom: 24px;
        }

        .plan-header h3 {
          font-size: 1.5rem;
          color: #2c3e50;
          margin-bottom: 16px;
        }

        .plan-price {
          font-size: 2rem;
          font-weight: 700;
          color: #059669;
        }

        .period-text {
          font-size: 1rem;
          color: #6b7280;
          font-weight: 400;
        }

        .plan-features {
          margin-bottom: 24px;
        }

        .feature {
          padding: 8px 0;
          color: #374151;
          font-size: 14px;
        }

        .feature.included {
          color: #059669;
        }

        .feature.excluded {
          color: #9ca3af;
        }

        .select-plan-btn {
          width: 100%;
          padding: 12px;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .plan-card:not(.selected) .select-plan-btn {
          background: #f3f4f6;
          color: #374151;
        }

        .plan-card:not(.selected) .select-plan-btn:hover {
          background: #e5e7eb;
        }

        .plan-card.selected .select-plan-btn {
          background: #059669;
          color: white;
        }

        .subscription-actions {
          text-align: center;
        }

        .start-subscription-btn {
          background: #059669;
          color: white;
          border: none;
          padding: 16px 32px;
          border-radius: 8px;
          font-size: 18px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .start-subscription-btn:hover {
          background: #047857;
        }

        .checkout-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .checkout-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .checkout-content h3 {
          margin-bottom: 20px;
          text-align: center;
          color: #2c3e50;
        }

        .auth-required {
          text-align: center;
          padding: 60px 20px;
          color: #6b7280;
        }

        .auth-required h2 {
          margin-bottom: 16px;
          color: #374151;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 50vh;
          text-align: center;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #059669;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }


        @media (max-width: 768px) {
          .plan-grid {
            grid-template-columns: 1fr;
          }
          
          .period-options {
            flex-direction: column;
            align-items: center;
          }

        }
      `}</style>
    </Layout>
  );
};

export default StripePaywallPage;
